import React from "react";
import { Box, Typography, Link as MuiLink, Divider } from "@mui/material";
import {
  Facebook,
  LinkedIn,
  Instagram,
  YouTube,
  WhatsApp,
  GitHub,
} from "@mui/icons-material";

import lucaStand from "../../../assets/images/original-3.webp";
import GradientSweep from "../../../components/AppBackground/GradientSweep";
import { useAmbient } from "../../../components/Motion/useAmbient";

const brandColors = {
  Facebook: "#1877F2",
  LinkedIn: "#0A66C2",
  Instagram: "#E1306C",
  YouTube: "#FF0000",
  WhatsApp: "#25D366",
  GitHub: "#333",
};

const getBrandColor = (icon) => {
  if (icon.type === Facebook) return brandColors.Facebook;
  if (icon.type === LinkedIn) return brandColors.LinkedIn;
  if (icon.type === Instagram) return brandColors.Instagram;
  if (icon.type === YouTube) return brandColors.YouTube;
  if (icon.type === WhatsApp) return brandColors.WhatsApp;
  if (icon.type === GitHub) return brandColors.GitHub;
  return "#fff";
};

const socials = [
  { icon: <Facebook />, link: "https://www.facebook.com/harrybabu93" },
  { icon: <LinkedIn />, link: "https://www.linkedin.com/in/findhari/" },
  {
    icon: <Instagram />,
    link: "https://www.instagram.com/stay__foolish___/",
  },
  {
    icon: <YouTube />,
    link: "https://www.youtube.com/c/Neram/videos",
  },
  {
    icon: <WhatsApp />,
    link: "https://api.whatsapp.com/send?phone=918807437399&text=Hi! Hari, I saw your porfolio and would like to talk to you regarding that.",
  },
  { icon: <GitHub />, link: "https://github.com/harrybabu93" },
];

// Social-icon treatment, split by INPUT CAPABILITY rather than viewport width so
// a mouse laptop keeps the hover effect and any touch device gets an always-on
// look (resizing a desktop window never breaks either one).
//
// Hover-capable (mouse): the icon rests in a MUTED brand tint and half-tucked
// behind the footer card (zIndex 1 < the card's 2). On hover it slides UP while
// staying behind the card, so it appears to emerge from behind the edge, fading
// to its vivid brand color with a single soft shadow. The zIndex never changes:
// z-index can't animate, so flipping it would make the icon snap fully in front
// for a frame instead of rising out smoothly. Staying behind the card keeps the
// lower part occluded as it lifts, which is what sells the "emerging" motion.
//
// Touch / no-hover: there's nothing to hover, so the icon is presented in its
// "alive" state by default - full brand color with a soft brand-tinted glow
// floating out from behind the card edge (gentle staggered pulse), and a quick
// press-bounce on tap. The icons stay in their tucked position so they read as
// brand-coloured glyphs peeking out from behind the footer edge.
const socialIconSx = (color, index) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  zIndex: 1,
  transition: "transform .3s ease, color .3s ease, filter .3s ease",

  // Hover-capable (mouse): muted brand tint at rest, emerge to vivid on hover.
  "@media (hover: hover) and (pointer: fine)": {
    color: `color-mix(in srgb, ${color} 45%, #6b7075)`,
    "&:hover": {
      transform: "translateY(-16px) scale(1.1)",
      color,
      filter: "drop-shadow(0 8px 12px rgba(0,0,0,0.20))",
    },
  },

  // Touch / no-hover: full brand color + soft glow halo floating behind, plus a
  // tactile press-bounce on tap.
  "@media (hover: none)": {
    color,
    "&::before": {
      content: '""',
      position: "absolute",
      inset: "-8px",
      borderRadius: "50%",
      background: `radial-gradient(circle, color-mix(in srgb, ${color} 38%, transparent) 0%, transparent 70%)`,
      zIndex: -1,
      animation: `haloPulse 3.6s ease-in-out ${(index % 6) * 0.3}s infinite`,
    },
    "&:active": { transform: "scale(0.88)", transition: "transform .12s ease" },
  },

  // Respect reduced-motion everywhere.
  "@media (prefers-reduced-motion: reduce)": {
    "&::before": { animation: "none" },
    "&:hover": { transform: "none" },
    "&:active": { transform: "none" },
  },
});

const Footer = () => {
  // Pauses the halo pulses and the card's gradient sweep while offscreen.
  const ambientRef = useAmbient();

  const renderIcon = (social, index) => {
    const color = getBrandColor(social.icon);
    return (
      <MuiLink
        key={index}
        href={social.link}
        target="_blank"
        rel="noopener noreferrer"
        underline="none"
        sx={{ display: "inline-block" }}
      >
        <Box sx={socialIconSx(color, index)}>
          {React.cloneElement(social.icon, { fontSize: "large" })}
        </Box>
      </MuiLink>
    );
  };

  return (
    <Box
      ref={ambientRef}
      component="footer"
      data-nav-bg="light"
      sx={{
        position: "relative",
        bgcolor: "#fff",
        pt: { xs: 2, md: 3 },
        bottom: "-30px",
      }}
    >
      {/* Social Icons - two groups flanking the centre image, tucked into the card */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: 2, md: 3 },
          position: "relative",
          // Tuck the row 13px into the card so the icons sit half-behind its top
          // edge: on hover (desktop) they emerge; on touch they read as
          // brand-coloured glyphs floating out from behind the edge with their
          // glow halo. This tuck also seats the doll's hand flush on the card.
          top: "13px",
          px: { xs: 2, md: 3 },
        }}
      >
        {/* Left group */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, md: 2 },
            pr: { xs: 4, md: 8 },
          }}
        >
          {socials.slice(0, 3).map((social, index) => renderIcon(social, index))}
        </Box>

        {/* Right group */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, md: 2 },
            pl: { xs: 4, md: 8 },
          }}
        >
          {socials
            .slice(3)
            .map((social, index) => renderIcon(social, index + 3))}
        </Box>
      </Box>

      {/* Center Image */}
      <Box
        component="img"
        src={lucaStand}
        alt="Luca"
        sx={{
          width: { xs: "120px", md: "150px" },
          position: "absolute",
          top: { xs: "-136px", md: "-176px" },
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 3,
        }}
      />
      <Box
        sx={{
          color: "white",
          position: "relative",
          zIndex: 2,
          // The card stays opaque (the social icons tuck behind its top edge),
          // so it carries its own copy of the brand sweep rather than showing
          // the page layer through. Its content sits above that sweep.
          "& > :not(.gradient-sweep)": { position: "relative", zIndex: 1 },
          // Pull the card up so its top edge half-covers the icon row (icons
          // tuck behind it, popping out on hover) and meets the doll's hand so it
          // rests on the card edge with no gap.
          mt: { xs: "-12px", md: "-14px" },
        }}
      >
        <GradientSweep virtualHeight={10} />

        {/* Description */}
        <Box
          sx={{
            textAlign: "center",
            pt: { xs: 3, md: 4 },
            px: { xs: 2, md: 3 },
          }}
        >
          <Typography
            sx={{
              fontFamily: "Kalam, cursive",
              fontSize: { xs: "14px", md: "16px" },
              lineHeight: 1.7,
              fontStyle: "italic",
              maxWidth: { xs: "320px", md: "500px" },
              mx: "auto",
              display: "block",
            }}
          >
            Senior Product Designer &amp; Frontend Engineer with 10 years
            shipping products at scale, from enterprise semiconductor
            manufacturing platforms to consumer EdTech reaching 1,000+ students
            across 3 countries. For opportunities, email:{" "}
            <MuiLink href="mailto:findhari93@gmail.com" color="inherit">
              findhari93@gmail.com
            </MuiLink>
          </Typography>

          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: { xs: "20px", md: "24px" },
              fontWeight: 700,
              mt: { xs: 2, md: 3 },
            }}
          >
            Exploring senior product design and frontend engineering roles
          </Typography>
        </Box>

        {/* Bottom section */}
        <Box sx={{ mt: 2, pb: { xs: 2, md: 3 }, textAlign: "center" }}>
          <Divider sx={{ bgcolor: "rgba(255,255,255,0.3)", my: 2 }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: { xs: 1, sm: 0 },
              px: { xs: 2, md: 4 },
              maxWidth: "900px",
              mx: "auto",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: { xs: "12px", md: "13px" },
              }}
            >
              Senior Product Designer &amp; Frontend Engineer
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: { xs: "12px", md: "13px" },
              }}
            >
              © 2026 Haribabu. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
